import {isFeatureEnabled} from 'feature-flags';
import {Mutator} from 'final-form';
import {Card, DataStatus, EmptyStatusAction, Separator} from 'platform/components';
import {Hide, Right, Show} from 'platform/foundation';

import {ReactNode, useCallback, useEffect, useMemo, useRef} from 'react';
import {FormSpy} from 'react-final-form';
import {useSelector} from 'react-redux';

import {isEmpty} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {carAuditApi, useCreateInspectionMutation} from '@dms/api/core';
import {vehicleApi} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {handleApiError, usePermissions} from '@dms/shared';

import {
  ApiError,
  appendSuffix,
  findFirstFocusableElement,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

import {useRouter} from '../../../hooks/useRouter';
import {useThunkDispatch} from '../../../hooks/useThunkDispatch';
import {updateAudit} from '../../../store/carAudit/actions';
import {updateAuditFields} from '../../../store/carAudit/reducer';
import {AuditFields} from '../../../store/carAudit/reducerUtils';
import {
  selectActiveAuditId,
  selectAudit,
  selectAuditFields,
  selectCarAudit,
  selectNavigationCategories,
  selectNavigationCategoriesStateParams,
  selectStructure,
} from '../../../store/carAudit/selectors';
import {AuditForm, TConditionForm, TConditionFormRenderProps} from '../../../types/ConditionTypes';
import {LoadAuditDataResponseItemBody} from '../../../types/LoadAuditDataResponseItemBody';
import {getUpdateRequestBodyFromFormData} from '../../../utils/getUpdateRequestBodyFromFormData';
import {noop} from '../../../utils/someTeasUtils';
import {AutoSave} from '../../FinalForm/components/AutoSave';
import {Form} from '../../FinalForm/components/Form';
import {FormApi} from '../../FinalForm/types/FormApi';
import {useVehicleCreateContext} from '../../VehicleCreateContext/hooks/useVehicleCreateContext';
import {useGetVehicleCondition} from '../hooks/useGetVehicleCondition';
import {useOverallComment} from '../hooks/useOverallComment';
import {NavigationState} from '../types/NavigationState';
import {areRequiredParamConditionsMet} from '../utils/areRequiredParamConditionsMet';
import {getCategoryKeyFromUri} from '../utils/getCategoryKeyFromUri';
import {getFormFieldName} from '../utils/getFormFieldName';
import {getFormSchema} from '../utils/getFormSchema';
import {getInitialFormValues} from '../utils/getInitialFormValues';
import {getRequiredFieldsFromCategories} from '../utils/getRequiredFieldsFromCategories';
import {AuditContext} from './AuditContext';
import {AuditHeader} from './AuditHeader';
import {AuditTabs} from './AuditTabs';
import {FinishInspection} from './FinishInspection';
import {GeneralComment} from './GeneralComment';
import {ToFinishingComment} from './ToFinishingComment';
import {VehicleConditionEmptyState} from './VehicleConditionEmptyState';
import {VideoUploadProvider} from './VideoUploadProvider';

export const Audit = (props: AuditProps) => {
  const {header, createVehicleFeatures, isCreate, ...rest} = props;

  const dispatch = useThunkDispatch();
  const auditStructure = useSelector(selectStructure);
  const auditId = useSelector(selectActiveAuditId);
  const audit = useSelector(selectAudit);
  const auditFields = useSelector(selectAuditFields);
  const isEditForToRevisionDisabled = isFeatureEnabled(featureFlags.INSPECTION_TO_REVISION_NO_EDIT);
  const isInNotEditableState = audit?.state === 'to_revision' && isEditForToRevisionDisabled;
  const navigationCategories = useSelector(selectNavigationCategories);
  const navigationCategoriesStateParams = useSelector(selectNavigationCategoriesStateParams);
  const router = useRouter();
  const {fieldName} = useOverallComment();

  const [permissionToCreateInspection, permissionToUpdateInspection] = usePermissions({
    permissionKeys: ['createInspection', 'updateInspection'],
    scopes: {
      updateInspection: {
        inspectionType: audit?.inspectionType,
        inspectionState: audit?.state,
        // Gated by inspection type/state, not participation
        participation: 'SKIP',
      },
      createInspection: {
        inspectionType: 'SKIP',
      },
    },
  });

  const {inspectionTab, conditionTab} = router.params;
  const activeTabUri = conditionTab ?? inspectionTab;

  const {setConditionForm} = useVehicleCreateContext();
  const formRef = useRef<FormApi<TConditionForm> | null>(null);
  const lastSetFormRef = useRef<FormApi<TConditionForm> | null>(null);

  useEffect(function clearFormRefsOnUnmount() {
    return () => {
      formRef.current = null;
      lastSetFormRef.current = null;
    };
  }, []);

  useEffect(
    function registerConditionForm() {
      if (formRef.current && setConditionForm) {
        // Only set form if this is actually a different form instance
        if (lastSetFormRef.current !== formRef.current) {
          lastSetFormRef.current = formRef.current;
          setConditionForm(formRef.current);
        }
      }
    },
    [setConditionForm]
  );

  let isAutoSaveDisabled = false;

  const setIsAutoSaveDisabled = (_isAutoSaveDisabled: boolean) => {
    isAutoSaveDisabled = _isAutoSaveDisabled;
  };

  const requiredFormFields = useMemo(
    () => getRequiredFieldsFromCategories(auditStructure?.categories),
    [auditStructure]
  );
  const isVehicleConditionEmptyState =
    Boolean(props.isVehicleCondition) &&
    isNotNil(auditId) &&
    isNotNil(auditStructure) &&
    isEmpty(navigationCategories ?? []);

  // The schema must honour the same conditions as getCategoriesWithError, otherwise a param that
  // is not on screen would still be reported as required by the form
  const schema = useMemo(
    () =>
      getFormSchema(
        Object.entries(requiredFormFields.fields)
          .filter(([, requiredParam]) =>
            areRequiredParamConditionsMet(audit?.fields, requiredParam)
          )
          .map(([fieldName]) => fieldName)
      ),
    [requiredFormFields, audit?.fields]
  );

  const initialValues = useMemo(
    () => getInitialFormValues(audit?.fields, navigationCategoriesStateParams),
    [audit?.fields, navigationCategoriesStateParams]
  );

  const getCurrentMainCategory = useCallback(() => {
    if (!activeTabUri) {
      return navigationCategories?.[0];
    }

    return navigationCategories?.find(
      (category) => category.uniqueKey === getCategoryKeyFromUri(String(activeTabUri))
    );
  }, [activeTabUri, navigationCategories]);

  const getChangedCategoryStateFieldValue = useCallback((): AuditFields => {
    const changedCategory = getCurrentMainCategory();

    if (changedCategory) {
      const fieldName = getFormFieldName(
        changedCategory.id,
        navigationCategoriesStateParams[changedCategory.id]
      );
      if (auditFields?.[fieldName] !== NavigationState.notFinished) {
        return {[fieldName]: NavigationState.notFinished};
      }
    }

    return {};
  }, [getCurrentMainCategory, navigationCategoriesStateParams, auditFields]);

  const updateFields = useCallback(
    async (formFields: AuditFields): Promise<void> => {
      await dispatch(updateAuditFields(formFields));

      if (!isCreate) {
        await dispatch(
          updateAudit.action({
            auditId: auditId || '',
            requestBody: getUpdateRequestBodyFromFormData(formFields),
          })
        );
        dispatch(vehicleApi.util.invalidateTags([{type: 'Vehicle', id: props.vehicleId ?? ''}]));
        dispatch(carAuditApi.util.invalidateTags([{type: 'AuditData', id: props.vehicleId ?? ''}]));
      }
    },
    [dispatch, isCreate, auditId, props.vehicleId]
  );

  const isDisabledAuditEdit = useCallback((): boolean => {
    const isDisabledForUser =
      audit?.inspectionType === LoadAuditDataResponseItemBody.inspectionType.VALIDATION_INSPECTION
        ? !permissionToCreateInspection ||
          (audit?.finishedAt !== null && !permissionToUpdateInspection)
        : !permissionToUpdateInspection;

    return isDisabledForUser;
  }, [
    audit?.inspectionType,
    audit?.finishedAt,
    permissionToCreateInspection,
    permissionToUpdateInspection,
  ]);

  const handleSave = async (
    values: AuditFields,
    form: FormApi<AuditForm, Partial<AuditForm>>
  ): Promise<void> => {
    // Not ideal, but since the autosave is about to be removed, this is a temporary solution - fixes https://carvago.atlassian.net/browse/T20-102643
    const actualyChangedValues = Object.fromEntries(
      Object.entries(values).filter(([key, value]) => {
        if (value !== '') {
          return true;
        }

        // Allow empty value if the field previously had a non-empty value (intentional clear)
        const previousValue = auditFields?.[key];
        return previousValue !== undefined && previousValue !== '';
      })
    );

    if (
      isAutoSaveDisabled ||
      form.getState().pristine ||
      isDisabledAuditEdit() ||
      !auditId ||
      isEmpty(actualyChangedValues)
    ) {
      return;
    }

    const valuesKeys = Object.keys(actualyChangedValues);
    const onlyOverallCommentChanged = valuesKeys.length === 1 && valuesKeys[0] === fieldName;

    // Overall comment is not in a category, because of that, when only the overall comment is
    // changed, the category state is not changed and should not be updated.
    const changedCategoryStateFieldValue = onlyOverallCommentChanged
      ? {}
      : getChangedCategoryStateFieldValue();

    const formFields = {
      ...actualyChangedValues,
      ...changedCategoryStateFieldValue,
    };

    await updateFields(formFields);
  };

  const auditBody = (
    <>
      <AuditHeader
        isInspection={props.isInspection}
        customHeaderContent={props.customHeaderContent}
        data-testid={suffixTestId('audit-header', rest)}
        isInNotEditableState={isInNotEditableState}
      />
      <Separator />
      <Show when={isVehicleConditionEmptyState}>
        <VehicleConditionEmptyState inspectionId={auditId ?? ''} />
      </Show>
      <Show when={!isVehicleConditionEmptyState}>
        <>
          <ToFinishingComment />
          <GeneralComment />

          <AuditTabs
            data-testid={suffixTestId('audit-tabs', rest)}
            onTabChange={props.onTabChange}
            activeTab={props.activeTab}
            createVehicleFeatures={createVehicleFeatures}
            isInspection={props.isInspection}
            vehicleId={props.vehicleId}
          />
          <Show when={props.customFooterContent}>{props.customFooterContent}</Show>
          <Show when={!props.customFooterContent && isNotNil(props.isCompletionDisabled)}>
            <Right>
              <FinishInspection
                buttonTitle={i18n.t('general.actions.complete')}
                showWhenFinished
                isDisabled={props.isCompletionDisabled}
              />
            </Right>
          </Show>
        </>
      </Show>
    </>
  );

  return (
    <AuditWrapper {...props} data-testid={suffixTestId('audit', rest)}>
      <Form<TConditionForm>
        formId={appendSuffix('auditForm', props['data-testid'])}
        initialValues={initialValues}
        isFormSaverDisabled
        onSubmit={noop}
        mutators={
          {
            setFieldValue: ([field, value], state, {changeValue}) => {
              changeValue(state, field, () => value);
            },
          } as Record<string, Mutator<TConditionForm>>
        }
        validateAfterSubmit
        schema={schema}
        render={(formRenderProps: TConditionFormRenderProps) => {
          // Capture form reference outside of render cycle
          if (formRenderProps.form && formRef.current !== formRenderProps.form) {
            formRef.current = formRenderProps.form;
          }

          return (
            <AuditContext
              {...{
                formRenderProps,
                requiredFormFields,
                isCreateVehicle: Boolean(props.isCreate),
                setIsAutoSaveDisabled,
                getChangedCategoryStateFieldValue,
                updateFields,
                isInNotEditableState,
              }}
            >
              <VideoUploadProvider auditId={auditId}>
                {props.onValuesChange ? (
                  <FormSpy
                    subscription={{values: true}}
                    onChange={(formState) => {
                      props.onValuesChange?.(formState.values);
                    }}
                  />
                ) : (
                  <AutoSave
                    save={handleSave}
                    saveOnInactiveBlur
                    debouncedTime={1000}
                    partial
                    saveOnChange={false}
                  />
                )}
                {Boolean(header) && header}
                <Hide when={props.isBodyOnly}>
                  <Card data-testid={suffixTestId('audit-card', rest)}>{auditBody}</Card>
                </Hide>
                <Show when={props.isBodyOnly}>{auditBody}</Show>
              </VideoUploadProvider>
            </AuditContext>
          );
        }}
      />
    </AuditWrapper>
  );
};

type AuditBaseProps = {
  isCreate?: boolean;
  notFoundText?: string;
  createVehicleFeatures?: string[];
  children?: ReactNode;
  vehicleId?: string;
  isVehicleCondition?: boolean;
} & TestIdProps;

const AuditWrapper = (props: AuditBaseProps) => {
  const {loading} = useSelector(selectCarAudit);
  const auditId = useSelector(selectActiveAuditId);
  const [createInspection, {isLoading: isCreatingInspection}] = useCreateInspectionMutation();
  const {getVehicleCondition: invalidateVehicleCondition, isLoading: isLoadingVehicleCondition} =
    useGetVehicleCondition();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(
    function focusFirstElementOnCreate() {
      const firstFocusable = findFirstFocusableElement(containerRef.current);

      if (props.isCreate && !loading.getNewestAuditStructure && firstFocusable) {
        firstFocusable.focus();
      }
    },
    [props.isCreate, loading.getNewestAuditStructure]
  );

  if (props.isCreate) {
    return (
      <DataStatus isLoading={loading.getNewestAuditStructure}>
        <div ref={containerRef}>{props.children}</div>
      </DataStatus>
    );
  }

  const handleCreateVehicleCondition = async () => {
    const vehicleId = props.vehicleId;
    if (!vehicleId) {
      return;
    }

    try {
      await createInspection({
        vehicleId,
        createAuditRequestBody: {inspectionType: 'vehicleCondition'},
      }).unwrap();

      await invalidateVehicleCondition(vehicleId);
    } catch (error) {
      handleApiError(error as ApiError);
    }
  };

  const actionForMissingVehicleCondition = props.isVehicleCondition
    ? ({
        title: i18n.t('entity.inspection.actions.createVehicleCondition'),
        variant: 'primary',
        isDisabled: isCreatingInspection || isLoadingVehicleCondition,
        onClick: handleCreateVehicleCondition,
      } as const satisfies EmptyStatusAction)
    : undefined;

  return (
    <DataStatus
      isLoading={
        loading.getAudit ||
        loading.getCondition ||
        loading.getAuditStructure ||
        isCreatingInspection ||
        isLoadingVehicleCondition
      }
      isEmpty={!auditId}
      emptyMessage={props.notFoundText}
      action={actionForMissingVehicleCondition}
    >
      {props.children}
    </DataStatus>
  );
};

type AuditProps = AuditBaseProps &
  TestIdProps & {
    onTabChange?: (id: string) => void;
    activeTab?: string;
    isInspection?: boolean;
    isBodyOnly?: boolean;
    isCompletionDisabled?: boolean;
    customHeaderContent?: ReactNode;
    customFooterContent?: ReactNode;
    header?: ReactNode;
    vehicleId?: string;
    onValuesChange?: (values: AuditFields) => void;
  };
